import { useState } from "react";
import { FLabel, FInput, FSelect, FTextarea, CheckBox, YesNo, SelectWithCustom } from "./FormControls";
import { DateField } from "./DateField";
import { Modal, Btn, Empty } from "../../components/UI";
import { Plus, Pencil, Trash2, FileX } from "lucide-react";
import {
  GENDERS, STATES, COUNTIES, TRANSPORTATION, MARITAL, EMPLOYMENT, CLIENT_STATUSES,
  INSURANCE_TYPES, PARTNER_TYPES, SEVERITIES, PLACEMENTS, REQUEST_TYPES,
  SERVICES, ADVERSE_REACTIONS, PP_QUESTIONS, CHARACTERISTICS, TOP_US_INSURERS, COMMON_MEDICATIONS,
} from "./constants";
import { usePrograms } from "../../lib/usePrograms";

const Opt = (arr) => arr.map((o) => <option key={o} value={o}>{o}</option>);

export function DemographicsSection({ data, set, companies }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
        <div><FLabel>First Name</FLabel><FInput data-testid="ref-first-name" value={data.first_name} onChange={f("first_name")} /></div>
        <div><FLabel>Middle Name</FLabel><FInput data-testid="ref-middle-name" value={data.middle_name} onChange={f("middle_name")} /></div>
        <div><FLabel>Last Name</FLabel><FInput data-testid="ref-last-name" value={data.last_name} onChange={f("last_name")} /></div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-5">
        <div><FLabel>Date of Birth</FLabel><DateField testId="ref-dob" value={data.dob} onChange={(v) => set({ dob: v })} /></div>
        <div><FLabel>Age</FLabel><FInput data-testid="ref-age" value={data.age} onChange={f("age")} /></div>
        <div><FLabel>Gender</FLabel><FSelect data-testid="ref-gender" value={data.gender} onChange={f("gender")}>{Opt(GENDERS)}</FSelect></div>
        <div><FLabel>Social Security Number</FLabel><FInput data-testid="ref-ssn" value={data.ssn} onChange={f("ssn")} /></div>
        <div><FLabel>Medicaid ID Number</FLabel><FInput value={data.medicaid_id} onChange={f("medicaid_id")} /></div>
        <div><FLabel>Ethnicity</FLabel><FInput value={data.ethnicity} onChange={f("ethnicity")} /></div>
        <div><FLabel>Marital Status</FLabel><FSelect value={data.marital_status} onChange={f("marital_status")}>{Opt(MARITAL)}</FSelect></div>
        <div><FLabel>Employment</FLabel><FSelect value={data.employment} onChange={f("employment")}>{Opt(EMPLOYMENT)}</FSelect></div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-5">
        <div><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
        <div><FLabel>City</FLabel><FInput value={data.city} onChange={f("city")} /></div>
        <div><FLabel>State</FLabel><FSelect value={data.state} onChange={f("state")}>{Opt(STATES)}</FSelect></div>
        <div><FLabel>Zip Code</FLabel><FInput value={data.zip} onChange={f("zip")} /></div>
        <div><FLabel>County</FLabel><FSelect value={data.county} onChange={f("county")}>{Opt(COUNTIES)}</FSelect></div>
        <div><FLabel>Transportation</FLabel><FSelect value={data.transportation} onChange={f("transportation")}>{Opt(TRANSPORTATION)}</FSelect></div>
        <div><FLabel>Home Phone Number</FLabel><FInput value={data.home_phone} onChange={f("home_phone")} /></div>
        <div><FLabel>Work Number</FLabel><FInput value={data.work_phone} onChange={f("work_phone")} /></div>
        <div><FLabel>Cell Number</FLabel><FInput value={data.cell_phone} onChange={f("cell_phone")} /></div>
        <div><FLabel>Email</FLabel><FInput data-testid="ref-email" type="email" value={data.email} onChange={f("email")} /></div>
      </div>
      <div className="mt-6">
        <FLabel>Client Status</FLabel>
        <div className="flex flex-wrap gap-x-7 gap-y-3 mt-1">
          {CLIENT_STATUSES.map(([v, l]) => (
            <CheckBox key={v} checked={data.client_status === v} onChange={() => set({ client_status: v })} label={l} testId={`ref-status-${v}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function InsuranceBlock({ title, data, set, companies }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  return (
    <div className="mt-6">
      <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-4">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-5">
        <div><FLabel>Insurance Company</FLabel>
          <SelectWithCustom testId={`ins-company-${title === "Primary Insurance Company" ? "primary" : "secondary"}`}
            value={data.company} onChange={(v) => set({ company: v })}
            options={[...new Set([...companies.map((c) => c.name), ...TOP_US_INSURERS])]}
            customPlaceholder="Enter insurance company name" />
        </div>
        <div><FLabel>Type of Insurance</FLabel><FSelect value={data.type} onChange={f("type")}>{Opt(INSURANCE_TYPES)}</FSelect></div>
        <div><FLabel>Identification Number</FLabel><FInput value={data.identification_number} onChange={f("identification_number")} /></div>
        <div><FLabel>Group Number</FLabel><FInput value={data.group_number} onChange={f("group_number")} /></div>
        <div><FLabel>Effective Date</FLabel><DateField value={data.effective_date} onChange={(v) => set({ effective_date: v })} /></div>
        <div><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
        <div><FLabel>Main Number</FLabel><FInput value={data.main_number} onChange={f("main_number")} /></div>
        <div><FLabel>Claims Department Number</FLabel><FInput value={data.claims_number} onChange={f("claims_number")} /></div>
        <div><FLabel>Fax</FLabel><FInput value={data.fax} onChange={f("fax")} /></div>
        <div><FLabel>Co-Payment</FLabel><FInput value={data.co_payment} onChange={f("co_payment")} /></div>
      </div>
    </div>
  );
}

export function InsuranceSection({ data, set, companies }) {
  return (
    <div>
      <FLabel>Choose Payer</FLabel>
      <div className="flex gap-7 mt-1">
        <CheckBox checked={data.payer === "insurance"} onChange={() => set({ payer: "insurance" })} label="Insurance Company" testId="payer-insurance" />
        <CheckBox checked={data.payer === "self_pay"} onChange={() => set({ payer: "self_pay" })} label="Self Pay" testId="payer-self-pay" />
      </div>
      {data.payer === "insurance" && (
        <>
          <InsuranceBlock title="Primary Insurance Company" data={data.primary} set={(v) => set({ primary: { ...data.primary, ...v } })} companies={companies} />
          <InsuranceBlock title="Secondary Insurance Company" data={data.secondary} set={(v) => set({ secondary: { ...data.secondary, ...v } })} companies={companies} />
        </>
      )}
    </div>
  );
}

export function ReferralSourceSection({ data, set }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
        <div><FLabel>Name of Referring Person</FLabel><FInput data-testid="ref-source-person" value={data.referring_person} onChange={f("referring_person")} /></div>
        <div><FLabel>Agency Title</FLabel><FInput value={data.agency_title} onChange={f("agency_title")} /></div>
        <div><FLabel>Relationship to Client</FLabel><FInput value={data.relationship} onChange={f("relationship")} /></div>
      </div>
      <div className="max-w-lg"><FLabel>Type of Partner/ Referral Source</FLabel><FSelect data-testid="ref-partner-type" value={data.partner_type} onChange={f("partner_type")}>{Opt(PARTNER_TYPES)}</FSelect></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <div><FLabel>Agency Name (if applicable)</FLabel><FInput value={data.agency_name} onChange={f("agency_name")} /></div>
        <div><FLabel>Agency Website</FLabel><FInput value={data.agency_website} onChange={f("agency_website")} /></div>
      </div>
      <div><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
        <div><FLabel>Business Phone</FLabel><FInput value={data.business_phone} onChange={f("business_phone")} /></div>
        <div><FLabel>Cell Phone</FLabel><FInput value={data.cell_phone} onChange={f("cell_phone")} /></div>
        <div><FLabel>Email Address</FLabel><FInput value={data.email} onChange={f("email")} /></div>
      </div>
    </div>
  );
}

export function PresentingProblemSection({ data, set }) {
  const setAnswer = (k, v) => {
    const q = PP_QUESTIONS.find((x) => x.key === k);
    const patch = { answers: { ...data.answers, [k]: v } };
    if (q?.specify && v === "no") patch[q.specify] = "";
    set(patch);
  };
  const cols = { left: PP_QUESTIONS.filter((q) => q.col === "left"), right: PP_QUESTIONS.filter((q) => q.col === "right") };
  const Question = ({ q }) => (
    <div className="mb-4 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4">
      <p className="text-[15px] leading-relaxed text-[#3E2723] mb-3">{q.label}</p>
      <YesNo value={data.answers[q.key] || ""} onChange={(v) => setAnswer(q.key, v)} testId={`pp-${q.key}`} />
      {q.specify && data.answers[q.key] === "yes" && (
        <div className="mt-3">
          <FSelect data-testid={`pp-${q.specify}`} value={data[q.specify]} onChange={(e) => set({ [q.specify]: e.target.value })}>{Opt(q.options)}</FSelect>
        </div>
      )}
    </div>
  );
  return (
    <div>
      <FLabel>Summarize reason(s) for seeking treatment</FLabel>
      <FTextarea data-testid="pp-summary" value={data.summary} onChange={(e) => set({ summary: e.target.value })} />
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-x-12">
        <div>{cols.left.map((q) => <Question key={q.key} q={q} />)}</div>
        <div>{cols.right.map((q) => <Question key={q.key} q={q} />)}</div>
      </div>
    </div>
  );
}

export function DiagnosisSection({ data, set }) {
  const [modal, setModal] = useState(false);
  const [entry, setEntry] = useState({ code: "", description: "" });
  const addEntry = () => {
    if (!entry.code && !entry.description) return;
    set({ entries: [...data.entries, entry] });
    setEntry({ code: "", description: "" }); setModal(false);
  };
  const setChar = (c, v) => set({ characteristics: { ...data.characteristics, [c]: v } });
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-heading text-lg font-bold text-[#3E2723]">Diagnosis</h4>
        <button type="button" data-testid="add-diagnosis-btn" onClick={() => setModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[#EDE4D7] px-4 py-2.5 text-sm font-medium text-[#6D4C41] hover:bg-[#E3D7C5] transition-colors">
          <Plus size={15} /> Add a Diagnosis
        </button>
      </div>
      {data.entries.length === 0 ? (
        <div className="py-10 flex flex-col items-center text-[#84675B]">
          <FileX size={44} strokeWidth={1.2} className="mb-3" />
          <span className="text-sm">No Diagnosis Entered</span>
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {data.entries.map((e, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-[#F5F2EB] px-4 py-2.5 text-sm">
              <span><span className="font-semibold">{e.code}</span> {e.description}</span>
              <button type="button" data-testid={`remove-diagnosis-${i}`} onClick={() => set({ entries: data.entries.filter((_, j) => j !== i) })} className="p-1.5 rounded-full hover:bg-white text-[#C62828]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div><FLabel>Primary Care M.D.</FLabel><FInput data-testid="dx-pcp" value={data.primary_care_md} onChange={(e) => set({ primary_care_md: e.target.value })} /></div>
        <div><FLabel>Phone</FLabel><FInput value={data.phone} onChange={(e) => set({ phone: e.target.value })} /></div>
      </div>
      <h4 className="font-heading text-sm font-bold text-[#3E2723] mt-8 mb-1">Other Characteristics of Persons Served</h4>
      <p className="text-sm text-[#84675B] mb-4">For each item, choose Yes or No.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {CHARACTERISTICS.map((c) => (
          <div key={c} className="flex items-center justify-between gap-4 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3">
            <p className="text-[15px] text-[#3E2723]">{c}</p>
            <YesNo value={data.characteristics[c] || ""} onChange={(v) => setChar(c, v)} testId={`char-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
          </div>
        ))}
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title="Add a Diagnosis">
        <div className="space-y-4">
          <div><FLabel>Diagnosis Code</FLabel><FInput data-testid="dx-code-input" value={entry.code} onChange={(e) => setEntry({ ...entry, code: e.target.value })} placeholder="e.g. F32.1" /></div>
          <div><FLabel>Description</FLabel><FInput data-testid="dx-desc-input" value={entry.description} onChange={(e) => setEntry({ ...entry, description: e.target.value })} placeholder="e.g. Major depressive disorder" /></div>
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="dx-save-btn" onClick={addEntry}>Add</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const EMPTY_MED = { start_date: "", medication: "", dose: "", md_prescribing: "", adverse_reaction: "" };

export function MedicationSection({ data, set }) {
  const [tab, setTab] = useState("active");
  const [modal, setModal] = useState(false);
  const [med, setMed] = useState(EMPTY_MED);
  const [editIdx, setEditIdx] = useState(null);
  const list = data[tab];

  const openAdd = () => { setMed(EMPTY_MED); setEditIdx(null); setModal(true); };
  const openEdit = (i) => { setMed(list[i]); setEditIdx(i); setModal(true); };
  const saveMed = () => {
    if (!med.medication) return;
    const next = editIdx === null ? [...list, med] : list.map((m, i) => (i === editIdx ? med : m));
    set({ [tab]: next }); setModal(false);
  };

  return (
    <div>
      <h4 className="font-heading text-lg font-bold text-[#3E2723] mb-4">Medication</h4>
      <div className="flex mb-5">
        {[["active", "Active Medication"], ["discontinued", "Discontinued Medications"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`med-tab-${v}`} onClick={() => setTab(v)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors ${tab === v ? "bg-[#D9C7B2] text-[#3E2723]" : "bg-[#F1EEE7] text-[#84675B]"} first:rounded-l-lg last:rounded-r-lg`}>
            {l}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-heading font-bold text-[#3E2723]">{tab === "active" ? "Active Medication" : "Discontinued Medications"}</h5>
        <button type="button" data-testid="add-medication-btn" onClick={openAdd}
          className="rounded-lg border border-[#E5DFD3] px-4 py-2.5 text-sm font-medium text-[#4E342E] hover:bg-[#F5F2EB] transition-colors">
          Add New Medication
        </button>
      </div>
      <div className="rounded-xl border border-[#EFEBE0] overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr>{["Start Date", "Medication", "Dose", "MD Prescribing", "Adverse Reaction", ""].map((h, i) => <th key={i} className="border-b border-[#EFEBE0] py-3 px-4 font-semibold text-[#3E2723]">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((m, i) => (
              <tr key={i} data-testid={`med-row-${i}`}>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{m.start_date}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{m.medication}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{m.dose}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{m.md_prescribing}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">{m.adverse_reaction}</td>
                <td className="border-b border-[#EFEBE0] py-3 px-4">
                  <div className="flex gap-2">
                    <button type="button" data-testid={`edit-med-${i}`} aria-label="Edit" onClick={() => openEdit(i)} className="h-8 w-8 rounded-lg bg-[#FBE9E7] text-[#7B6157] flex items-center justify-center hover:bg-[#F5DCD7]"><Pencil size={13} /></button>
                    <button type="button" data-testid={`delete-med-${i}`} aria-label="Delete" onClick={() => set({ [tab]: list.filter((_, j) => j !== i) })} className="h-8 w-8 rounded-lg bg-[#FFEBEE] text-[#C62828] flex items-center justify-center hover:bg-[#FFDDE1]"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <Empty text="No medications added" />}
      </div>
      <div className="mt-5"><FLabel>Note for</FLabel><FTextarea rows={4} value={data.note} onChange={(e) => set({ note: e.target.value })} /></div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
        <div><FLabel>Side Effects Severity</FLabel><FSelect data-testid="med-severity" value={data.side_effects_severity} onChange={(e) => set({ side_effects_severity: e.target.value })}>{Opt(SEVERITIES)}</FSelect></div>
        <div className="pb-3"><CheckBox checked={data.reconciliation_completed} onChange={() => set({ reconciliation_completed: !data.reconciliation_completed })} label="Medication Reconciliation Completed" testId="med-reconciliation" /></div>
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={editIdx === null ? "Add New Medication" : "Edit Medication"}>
        <div className="space-y-4">
          <div><FLabel>Start Date</FLabel><DateField testId="med-start-input" value={med.start_date} onChange={(v) => setMed({ ...med, start_date: v })} /></div>
          <div><FLabel>Medication</FLabel>
            <SelectWithCustom testId="med-name-input" value={med.medication} onChange={(v) => setMed({ ...med, medication: v })}
              options={COMMON_MEDICATIONS} customPlaceholder="Enter medication name" />
          </div>
          <div><FLabel>Dose</FLabel><FInput data-testid="med-dose-input" value={med.dose} onChange={(e) => setMed({ ...med, dose: e.target.value })} /></div>
          <div><FLabel>MD Prescribing</FLabel><FInput value={med.md_prescribing} onChange={(e) => setMed({ ...med, md_prescribing: e.target.value })} /></div>
          <div><FLabel>Adverse Reaction</FLabel><FSelect value={med.adverse_reaction} onChange={(e) => setMed({ ...med, adverse_reaction: e.target.value })}>{Opt(ADVERSE_REACTIONS)}</FSelect></div>
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="med-save-btn" onClick={saveMed}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function HousingSection({ data, set }) {
  const f = (k) => (e) => set({ [k]: e.target.value });
  const toggle = (p) => set({ placement: data.placement.includes(p) ? data.placement.filter((x) => x !== p) : [...data.placement, p] });
  return (
    <div>
      <FLabel>Placement</FLabel>
      <div className="flex flex-wrap gap-x-7 gap-y-3 mt-1 mb-6">
        {PLACEMENTS.map((p) => (
          <CheckBox key={p} checked={data.placement.includes(p)} onChange={() => toggle(p)} label={p} testId={`placement-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
        <div><FLabel>Name of Contact Person</FLabel><FInput data-testid="housing-contact-name" value={data.contact_name} onChange={f("contact_name")} /></div>
        <div><FLabel>Relationship</FLabel><FInput value={data.relationship} onChange={f("relationship")} /></div>
        <div><FLabel>Name of Facility (if applicable)</FLabel><FInput value={data.facility_name} onChange={f("facility_name")} /></div>
      </div>
      <div className="mt-5"><FLabel>Address</FLabel><FInput value={data.address} onChange={f("address")} /></div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
        <div><FLabel>Contact Person's Home Phone</FLabel><FInput value={data.home_phone} onChange={f("home_phone")} /></div>
        <div><FLabel>Mobile</FLabel><FInput value={data.mobile} onChange={f("mobile")} /></div>
        <div><FLabel>Contact Person's e-mail</FLabel><FInput value={data.email} onChange={f("email")} /></div>
      </div>
    </div>
  );
}

export function RequestedServicesSection({ data, set }) {
  const PROGRAMS = usePrograms();
  const toggle = (t) => set({ request_types: data.request_types.includes(t) ? data.request_types.filter((x) => x !== t) : [...data.request_types, t] });
  const setRow = (i, k, v) => set({ services: data.services.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const removeRow = (i) => set({ services: data.services.filter((_, j) => j !== i) });
  return (
    <div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
        <div className="xl:col-span-2 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-5">
          <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-1">Request for Services</h4>
          <p className="text-sm text-[#84675B] mb-4">Select all that apply.</p>
          <div className="flex flex-col gap-2.5">
            {REQUEST_TYPES.map((t) => (
              <CheckBox key={t} checked={data.request_types.includes(t)} onChange={() => toggle(t)} label={t}
                testId={`req-type-${REQUEST_TYPES.indexOf(t)}`} className="w-full rounded-xl py-2.5" />
            ))}
          </div>
        </div>
        <div className="xl:col-span-3">
          <h4 className="font-heading text-sm font-bold text-[#3E2723] mb-1">Requested Programs & Services</h4>
          <p className="text-sm text-[#84675B] mb-4">Add one row per requested service.</p>
          <div className="flex flex-col gap-3">
            {data.services.map((row, i) => (
              <div key={i} className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-4 items-end">
                  <div><FLabel>Requested Program</FLabel><FSelect data-testid={`req-program-${i}`} value={row.program} onChange={(e) => setRow(i, "program", e.target.value)}>{Opt(PROGRAMS)}</FSelect></div>
                  <div><FLabel>Requested Service</FLabel><FSelect data-testid={`req-service-${i}`} value={row.service} onChange={(e) => setRow(i, "service", e.target.value)}>{Opt(SERVICES)}</FSelect></div>
                  <div><FLabel>Start Date</FLabel><DateField testId={`req-start-date-${i}`} value={row.start_date} onChange={(v) => setRow(i, "start_date", v)} /></div>
                  <button type="button" data-testid={`req-remove-${i}`} aria-label={`Remove row ${i + 1}`} onClick={() => removeRow(i)}
                    disabled={data.services.length === 1}
                    className="h-11 w-11 shrink-0 rounded-xl border border-[#E5DFD3] text-[#84675B] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828] hover:border-[#FFCDD2] transition-colors disabled:opacity-40 disabled:pointer-events-none">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="req-add-more-btn" onClick={() => set({ services: [...data.services, { program: "", service: "", start_date: "" }] })}
            className="mt-3 flex items-center gap-2 rounded-xl border border-dashed border-[#C9BEB0] px-5 py-3 text-sm font-semibold text-[#6D4C41] hover:bg-[#F5F2EB] hover:border-[#8D6E63] transition-colors w-full justify-center">
            <Plus size={15} /> Add More
          </button>
        </div>
      </div>
      <div className="mt-8"><FLabel>Justification for Request</FLabel><FTextarea data-testid="req-justification" rows={6} value={data.justification} onChange={(e) => set({ justification: e.target.value })} /></div>
    </div>
  );
}

export function AdditionalInfoSection({ data, set }) {
  return (
    <div>
      <FLabel>Additional Information</FLabel>
      <FTextarea data-testid="additional-info-text" rows={14} value={data.text} onChange={(e) => set({ text: e.target.value })} />
      <div className="mt-5">
        <FLabel>Received in Paper Copy</FLabel>
        <div className="mt-1"><YesNo value={data.received_paper_copy} onChange={(v) => set({ received_paper_copy: v })} testId="paper-copy" /></div>
      </div>
    </div>
  );
}
